// Phase 1.5 item #1 · Replay Turn overlay
//
// 3-column modal: [edited system prompt] | [original reply + tokens/latency]
// | [replayed reply + tokens/latency + diff card]
//
// Powered by POST /api/conversations/{id}/replay (backend wired in
// Phase 1 sub-task G). Admin-only; gating done at the caller
// (TurnRow renders the "Replay turn" button only for admin role).
//
// Production-grade upgrade (2026-06-23): Monaco DiffEditor replaces
// the initial textarea. Monaco is already in deps (@monaco-editor/react,
// used by Builder), so no new bundle cost. Side-by-side diff with
// inline change highlighting, VSCode-grade editor with find/undo/
// folding, theme follows OS prefers-color-scheme.
import React, { useState, useMemo, useEffect } from "react";
import { COLORS } from "../../../theme/colors";
import { conversationInspectorApi } from "../../../api/client";
import { MarkdownText } from "../../../components/MarkdownText";
import { ReplayDiffCard } from "./ReplayDiffCard";
import { PromptDiffEditor } from "./PromptDiffEditor";

export function ReplayTurnOverlay({ conversationId, turn, onClose }) {
  // Extract trace_id from the turn payload + observation_id from the
  // user-visible-reply GENERATION span.
  //
  // 2026-06-25 fix — pre-fix used `spans.find(type === "GENERATION")`
  // which picks the FIRST GENERATION span. On a supervisor turn that
  // is the L2 lead agent's intermediate tool-call JSON decision
  // ({"tool": ..., "arguments": {...}}) — not the natural-language
  // reply the user actually saw. Both the "ORIGINAL REPLY" and
  // "REPLAYED REPLY" columns then showed raw tool-call JSON, which
  // is correct content for THAT observation but useless for the
  // operator who wants to test prompt edits against the final reply.
  //
  // Fix: prefer the LAST GENERATION span (the synthesis call). On
  // regular agents there's only one GENERATION so the behavior is
  // unchanged; on supervisor agents we land on the synthesis call
  // whose content IS user-visible text.
  const traceId = turn?.langfuse_trace_id || null;
  // Pick the GENERATION span that produced the user-visible reply.
  //
  // On a v13 supervisor turn the span tree looks like:
  //   supervisor.l1                    [no GENERATION]
  //   supervisor.l2
  //     └─ ChatCompletion              [GENERATION — L2 routing JSON]
  //   supervisor.l3_<mode>
  //     └─ subagent.<expert_name>
  //         ├─ ChatCompletion          [GENERATION — expert's intermediate]
  //         └─ ChatCompletion          [GENERATION — expert's final answer ← what we want]
  //   supervisor.l3_synthesis (fan_out only)
  //     └─ ChatCompletion              [GENERATION — synthesis ← preferred when present]
  //
  // The user-visible reply comes from:
  //   - fan_out mode    → synthesis GENERATION
  //   - other modes     → the expert subagent's LAST GENERATION (the final answer)
  //   - regular agent   → the only GENERATION
  //
  // Picker preference:
  //   1. GENERATION descended from a `supervisor.l3_synthesis` span
  //   2. GENERATION descended from a `subagent.*` span (LAST one = final answer)
  //   3. GENERATION whose output substring-matches assistant_message.content
  //   4. First GENERATION (regular agent fallback)
  const generation = useMemo(() => {
    const spans = turn?.spans || [];
    const gens = spans.filter(s => s.type === "GENERATION");
    if (gens.length === 0) return undefined;
    if (gens.length === 1) return gens[0];

    // Build a parent_id → ancestor-name resolver
    const byId = new Map(spans.map(s => [s.id, s]));
    const findAncestor = (span, predicate, maxDepth = 8) => {
      let cur = span;
      let depth = 0;
      while (cur && depth < maxDepth) {
        if (predicate(cur)) return cur;
        if (!cur.parent_id) return null;
        cur = byId.get(cur.parent_id);
        depth += 1;
      }
      return null;
    };

    // Among subagent-descended GENERATIONs, the EXPERT'S ANSWER CALL
    // is the one with the most prompt context (full system prompt +
    // history + RAG chunks). Classifier / sub-question / sentiment
    // calls take small prompts (40-340 tokens). Answer calls take
    // 500-2000+ tokens. Pick by max prompt_tokens.
    const promptTokens = (s) => {
      const t = s.prompt_tokens || (s.usage && s.usage.prompt_tokens) || 0;
      return typeof t === "number" ? t : 0;
    };

    // 1. Prefer GENERATIONs descended from supervisor.l3_synthesis
    const synthGens = gens.filter(g =>
      findAncestor(g, s => /synth/i.test(s.name || ""))
    );
    if (synthGens.length > 0) return synthGens[synthGens.length - 1];

    // 2. Prefer subagent-descended GENERATIONs. The subagent makes
    //    MULTIPLE LLM calls (classifier / sub-question / answer); the
    //    answer call has the most prompt tokens because it carries the
    //    full system prompt + history + RAG. Sort desc by prompt_tokens.
    const subagentGens = gens.filter(g =>
      findAncestor(g, s => /^subagent\./.test(s.name || ""))
    );
    if (subagentGens.length > 0) {
      const sorted = [...subagentGens].sort((a, b) => promptTokens(b) - promptTokens(a));
      return sorted[0];
    }

    // 3. Content match against assistant_message.content (strip the
    //    `[Expert: X]` prefix that the supervisor adds in _wrap()).
    const assistantText = (turn?.assistant_message?.content || "")
      .replace(/^\[Expert:[^\]]+\]\s*/, "").trim();
    if (assistantText.length >= 16) {
      const probe = assistantText.slice(0, Math.min(60, assistantText.length));
      const matched = gens.find(s => {
        const out = String(s.output_preview || s.output || "");
        return out && out.includes(probe);
      });
      if (matched) return matched;
    }

    // 4. Highest prompt_tokens GENERATION — for regular agents the call
    //    with the full system prompt is the answer call; small-prompt
    //    GENERATIONs are internal helpers (intent classifier, etc.).
    const sorted = [...gens].sort((a, b) => promptTokens(b) - promptTokens(a));
    return sorted[0];
  }, [turn?.spans, turn?.assistant_message?.content]);
  const observationId = generation?.id || null;

  // Phase E observation-label — show the operator WHICH LLM call is
  // being replayed. Walk the parent chain to find the most specific
  // semantic span (subagent.X > supervisor.l3_X > supervisor.l2/l1 >
  // bare ChatCompletion).
  const observationLabel = useMemo(() => {
    if (!generation) return null;
    const spans = turn?.spans || [];
    const byId = new Map(spans.map(s => [s.id, s]));
    let cur = generation;
    let depth = 0;
    while (cur && depth < 8) {
      const n = cur.name || "";
      if (/^subagent\./.test(n)) return `expert subagent · ${n.replace(/^subagent\./, "")}`;
      if (/synth/i.test(n)) return "L3 synthesis";
      if (n === "supervisor.l2") return "L2 lead agent (routing decision)";
      if (n === "supervisor.l1") return "L1 router";
      if (!cur.parent_id) break;
      cur = byId.get(cur.parent_id);
      depth += 1;
    }
    return "LLM generation";
  }, [generation, turn?.spans]);

  // First pass: best-effort extract from the (possibly truncated)
  // inspector payload so the editor isn't empty for a millisecond
  // when the modal opens. Then immediately fetch the FULL prompt
  // from the backend (which bypasses the 2000-char input truncation
  // the inspector aggregator does on every span).
  const stubPrompt = useMemo(() => extractSystemPrompt(generation), [generation]);

  const [originalPrompt, setOriginalPrompt] = useState(stubPrompt);
  const [editedPrompt, setEditedPrompt] = useState(stubPrompt);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // Phase 1.5 #1 polish · Edit / Preview toggle. System prompts are
  // markdown — the diff editor stays the source-of-truth for editing
  // (line-by-line diff is exact), but a Preview mode renders the
  // modified side as Markdown so the user can READ it the way the
  // LLM sees it.
  const [viewMode, setViewMode] = useState("edit");  // "edit" | "preview"
  // Start true so the very-first render shows the loading placeholder
  // (not the DiffEditor with empty values) — that initial-render swap
  // was the trigger for Monaco's "TextModel got disposed before
  // DiffEditorWidget model got reset" error.
  const [promptLoading, setPromptLoading] = useState(Boolean(traceId && observationId));

  // Phase 1.5 #1 follow-up · fetch the full original system prompt on
  // open. The inspector payload truncates span input to 2000 chars; a
  // typical domain-Supervisor prompt is 5–15K chars and would parse-
  // fail in the frontend. This endpoint hits Langfuse directly for the
  // untruncated value.
  useEffect(() => {
    if (!conversationId || !observationId) return;
    let cancelled = false;
    setPromptLoading(true);
    (async () => {
      try {
        const res = await conversationInspectorApi.getReplayOriginalPrompt(
          conversationId, observationId,
        );
        if (cancelled) return;
        const full = res?.data?.system_prompt ?? "";
        if (full && full.length > 0) {
          setOriginalPrompt(full);
          // Only overwrite the edited buffer if the user hasn't typed
          // yet (i.e. it still matches the stub we initialized from).
          setEditedPrompt(prev => (prev === stubPrompt ? full : prev));
        }
      } catch {
        // Non-fatal — fall back to the stub. Error toast appears via
        // canReplay disabled state when prompt is empty.
      } finally {
        if (!cancelled) setPromptLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [conversationId, observationId, stubPrompt]);

  const canReplay = traceId && observationId && editedPrompt && editedPrompt.trim().length > 0 && !promptLoading;
  const isUnchanged = editedPrompt === originalPrompt;

  const onRun = async () => {
    if (!canReplay) return;
    setBusy(true);
    setError(null);
    try {
      const res = await conversationInspectorApi.replayTurn(
        conversationId, traceId, observationId, editedPrompt
      );
      if (res?.success === false) {
        setError(res.error || res.detail || "Replay failed");
      } else {
        setResult(res?.data || res);
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1300px, 96vw)",
          height: "min(85vh, 800px)",
          background: COLORS.bg,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
          display: "flex", flexDirection: "column",
          boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 18px", borderBottom: `1px solid ${COLORS.border}`,
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
              Replay turn
            </div>
            <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>
              Edit system prompt → re-run against same tools + retrieved chunks → compare
            </div>
            {observationLabel && (
              <div
                data-testid="replay-observation-label"
                style={{
                  fontSize: 10, color: COLORS.accentText || COLORS.accent || "#a78bfa",
                  marginTop: 4, fontStyle: "italic",
                }}
              >
                Replaying: <strong>{observationLabel}</strong>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={onRun}
              disabled={!canReplay || busy}
              style={{
                padding: "6px 14px", fontSize: 12, fontWeight: 600,
                borderRadius: 5, cursor: canReplay && !busy ? "pointer" : "not-allowed",
                background: canReplay && !busy ? (COLORS.accent || "#7c3aed") : COLORS.surface,
                color: canReplay && !busy ? "#fff" : COLORS.textMuted,
                border: canReplay && !busy ? "none" : `1px solid ${COLORS.border}`,
              }}
            >
              {busy ? "Running…" : "Run replay"}
            </button>
            <button
              onClick={onClose}
              style={{
                padding: "6px 12px", fontSize: 12, fontWeight: 600,
                borderRadius: 5, cursor: "pointer",
                background: COLORS.surface, color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Body */}
        {!traceId || !observationId ? (
          <EmptyState
            title="No replayable LLM call in this turn"
            detail={
              !traceId
                ? "This turn has no Langfuse trace — most likely a pre-instrumentation conversation."
                : "Replay requires an LLM generation observation, but none was found in this turn's spans."
            }
          />
        ) : (
          <div style={{ flex: 1, display: "grid",
                        gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
                        minHeight: 0, gap: 0 }}>
            {/* Left — Prompt editor (Monaco DiffEditor: original | edited) */}
            <Column
              title="System prompt · original (left) ↔ edited (right)"
              subtitle={
                promptLoading
                  ? "loading full prompt from Langfuse…"
                  : isUnchanged
                    ? `${(originalPrompt || "").length} chars · no changes yet`
                    : `${editedPrompt.length} chars · edits highlighted`
              }
              headerExtra={
                !promptLoading && (
                  <ModeToggle
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                      { value: "edit", label: "Edit (diff)" },
                      { value: "preview", label: "Preview (markdown)" },
                    ]}
                  />
                )
              }
            >
              {/* Only mount Monaco AFTER the prompt load resolves so the
                  DiffEditor doesn't swap its TextModel mid-mount. That
                  race triggers the upstream Monaco bug "TextModel got
                  disposed before DiffEditorWidget model got reset". */}
              {promptLoading ? (
                <div style={{
                  flex: 1, display: "flex", alignItems: "center",
                  justifyContent: "center",
                  color: COLORS.textMuted, fontSize: 12,
                  border: `1px solid ${COLORS.border}`, borderRadius: 4,
                }}>Loading prompt from Langfuse…</div>
              ) : (
                // Both views MUST stay mounted across mode switches —
                // toggling visibility via CSS instead of conditional
                // render keeps Monaco's TextModel alive. Unmounting the
                // DiffEditor on toggle triggers the upstream race
                // "TextModel got disposed before DiffEditorWidget model
                // got reset" because @monaco-editor/react's dispose
                // chain fires after the wrapper unmounts. CSS-hide
                // sidesteps it entirely: editor mounts once, lives for
                // the modal's lifetime, disposes once on Close.
                <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex" }}>
                  <div style={{
                    flex: 1, minHeight: 0, display: viewMode === "edit" ? "flex" : "none",
                    flexDirection: "column",
                  }}>
                    <PromptDiffEditor
                      original={originalPrompt || ""}
                      modified={editedPrompt || ""}
                      onChange={setEditedPrompt}
                    />
                  </div>
                  <div style={{
                    flex: 1, minHeight: 0, display: viewMode === "preview" ? "flex" : "none",
                    flexDirection: "column",
                  }}>
                    <PromptPreview
                      original={originalPrompt || ""}
                      modified={editedPrompt || ""}
                    />
                  </div>
                </div>
              )}
            </Column>

            {/* Right — Replies stacked (original above, replayed below) */}
            <div style={{
              display: "flex", flexDirection: "column",
              minHeight: 0,
            }}>
              <Column title="Original reply" subtitle={
                result
                  ? `${result.original_tokens_in || 0}→${result.original_tokens_out || 0} tokens · ${formatDur(result.original_duration_ms)}`
                  : "ran with the current production prompt"
              } half>
                <ReplyBox
                  content={(result?.original_output) || (turn?.assistant_message?.content) || ""}
                />
              </Column>
              <Column title="Replayed reply" subtitle={
                result
                  ? `${result.replayed_tokens_in || 0}→${result.replayed_tokens_out || 0} tokens · ${formatDur(result.replayed_duration_ms)}`
                  : busy ? "running…" : "click Run replay to populate"
              } half>
                <ReplyBox
                  content={result?.replayed_output || ""}
                  placeholder={busy ? "Replaying…" : "—"}
                />
              </Column>
            </div>
          </div>
        )}

        {/* Diff card footer */}
        {result && (
          <div style={{
            padding: "10px 18px", borderTop: `1px solid ${COLORS.border}`,
            flexShrink: 0,
          }}>
            <ReplayDiffCard result={result} />
          </div>
        )}

        {/* Error toast */}
        {error && (
          <div style={{
            padding: "10px 18px", borderTop: `1px solid ${COLORS.border}`,
            background: "#fef2f2", color: "#991b1b", fontSize: 12, flexShrink: 0,
          }}>
            ⚠ {error}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── helpers / sub-components ──────────────────────────────────────

function Column({ title, subtitle, children, half = false, headerExtra }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      minHeight: 0, padding: "10px 12px",
      borderRight: half ? "none" : `1px solid ${COLORS.border}`,
      borderBottom: half ? `1px solid ${COLORS.border}` : "none",
      flex: half ? 1 : "1 1 auto",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, fontSize: 10, fontWeight: 700, color: COLORS.textMuted,
                      textTransform: "uppercase", letterSpacing: 0.4 }}>
          {title}
        </div>
        {headerExtra}
      </div>
      <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 1, marginBottom: 6,
                    minHeight: 13 }}>
        {subtitle || " "}
      </div>
      {children}
    </div>
  );
}

// Phase 1.5 #1 polish · small Edit / Preview toggle for the prompt column.
function ModeToggle({ value, onChange, options }) {
  return (
    <div
      style={{
        display: "inline-flex",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 5,
        overflow: "hidden",
      }}
      data-testid="prompt-mode-toggle"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            data-testid={`prompt-mode-${opt.value}`}
            style={{
              padding: "3px 8px",
              fontSize: 10,
              fontWeight: 600,
              background: active ? (COLORS.accent || "#7c3aed") : "transparent",
              color: active ? "#fff" : COLORS.textMuted,
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// Markdown-rendered preview — diff editor is for editing, preview is
// for reading the prompt the way the LLM sees it.
function PromptPreview({ original, modified }) {
  const [side, setSide] = useState("modified");
  const text = side === "original" ? original : modified;
  return (
    <div
      style={{
        flex: 1, minHeight: 0,
        display: "flex", flexDirection: "column",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 4, overflow: "hidden",
      }}
      data-testid="prompt-preview"
    >
      <div
        style={{
          display: "flex", gap: 4,
          padding: "5px 8px",
          background: COLORS.surfaceMuted || COLORS.bg,
          borderBottom: `1px solid ${COLORS.border}`,
          fontSize: 10,
        }}
      >
        <button type="button" onClick={() => setSide("modified")}
          style={pillBtnStyle(side === "modified")}
          data-testid="preview-side-modified">Modified</button>
        <button type="button" onClick={() => setSide("original")}
          style={pillBtnStyle(side === "original")}
          data-testid="preview-side-original">Original</button>
        <div style={{ flex: 1 }} />
        <div style={{ color: COLORS.textMuted, alignSelf: "center", fontSize: 10 }}>
          {(text || "").length} chars
        </div>
      </div>
      <div
        style={{
          flex: 1, overflow: "auto", padding: 12,
          fontSize: 12, lineHeight: 1.55,
          background: COLORS.surface, color: COLORS.text,
        }}
      >
        {text && text.trim().length > 0 ? (
          <MarkdownText text={text} />
        ) : (
          <div style={{ color: COLORS.textMuted, fontStyle: "italic" }}>(empty)</div>
        )}
      </div>
    </div>
  );
}

function pillBtnStyle(active) {
  return {
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 600,
    background: active ? (COLORS.accent || "#7c3aed") : "transparent",
    color: active ? "#fff" : COLORS.textMuted,
    border: `1px solid ${active ? "transparent" : COLORS.border}`,
    borderRadius: 4,
    cursor: "pointer",
    fontFamily: "inherit",
  };
}

function ReplyBox({ content, placeholder }) {
  if (!content) {
    return (
      <div style={{
        flex: 1, padding: 10, fontSize: 12, color: COLORS.textMuted,
        fontStyle: "italic", overflow: "auto",
        background: COLORS.surfaceMuted || COLORS.bg,
        border: `1px solid ${COLORS.border}`, borderRadius: 4,
      }}>{placeholder || "—"}</div>
    );
  }
  return (
    <div style={{
      flex: 1, padding: 10, fontSize: 12, color: COLORS.text,
      overflow: "auto",
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`, borderRadius: 4,
      lineHeight: 1.55,
    }}>
      <MarkdownText text={content} />
    </div>
  );
}

function EmptyState({ title, detail }) {
  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: 40, color: COLORS.textMuted, gap: 8,
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{title}</div>
      <div style={{ fontSize: 12, maxWidth: 480, textAlign: "center" }}>{detail}</div>
    </div>
  );
}

// Exported for unit testing (repo pattern: expose pure logic from a component file).
export function extractSystemPrompt(generation) {
  if (!generation) return "";
  // Try input_preview (Inspector aggregator's JSON-encoded delta)
  const ip = generation.input_preview;
  if (ip) {
    try {
      const parsed = typeof ip === "string" ? JSON.parse(ip) : ip;
      const msgs = parsed?.messages;
      if (Array.isArray(msgs)) {
        const sys = msgs.find(m => m?.role === "system");
        if (sys?.content) return sys.content;
      }
    } catch { /* not JSON; fall through */ }
  }
  // Try OpenInference attribute shape — llm.input_messages.0.message.content
  const a = generation.attributes || {};
  const otelAttrs = typeof a.attributes === "string" ? safeJson(a.attributes) : null;
  if (otelAttrs) {
    const sysContent = otelAttrs["llm.input_messages.0.message.content"];
    if (sysContent) return sysContent;
  }
  return "";
}

export function safeJson(s) {
  try { return JSON.parse(s); } catch { return null; }
}

export function formatDur(ms) {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}
